import { useEffect, useState, type FormEvent } from 'react';
import { Eye, EyeOff, Loader2, Lock, ShieldCheck } from 'lucide-react';
import { getSetupState, runInitialSetup, signIn } from '@generated/server/auth';
import { useSession } from './session';
import { Logo } from './Logo';
import { TokenReveal } from './TokenReveal';

export function LoginPage() {
  const { setSession } = useSession();
  const [username, setUsername] = useState('');
  const [token, setToken] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [needsSetup, setNeedsSetup] = useState(false);
  const [revealed, setRevealed] = useState<{ username: string; token: string } | null>(null);

  useEffect(() => {
    getSetupState({ data: {} }).then((r) => setNeedsSetup(!!r?.needsSetup)).catch((e) => console.error(e));
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const r = await signIn({ data: { username, token } });
      if (r?.sid) setSession(r.sid, r.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign in failed.');
    } finally {
      setBusy(false);
    }
  }

  async function setup() {
    setBusy(true);
    try {
      const r = await runInitialSetup({ data: {} });
      setRevealed(r);
      setNeedsSetup(false);
      setUsername(r.username);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Setup failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4">
      <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-secondary/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-primary/15 blur-3xl" />
      <div className="relative w-full max-w-md rounded-2xl bg-card p-8 shadow-2xl shadow-secondary/10 ring-1 ring-border">
        <div className="mb-6 flex justify-center"><Logo size="lg" /></div>
        <h1 className="text-center text-2xl font-bold tracking-tight text-secondary">Welcome back</h1>
        <p className="mt-1 text-center text-sm text-muted-foreground">Sign in with your username and access token</p>
        <form onSubmit={submit} className="mt-7 space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Username</span>
            <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" className="glass-input h-11 w-full rounded-xl px-3 text-sm outline-none" placeholder="firstname.lastname" required />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground">Access token</span>
            <div className="relative">
              <input type={show ? 'text' : 'password'} value={token} onChange={(e) => setToken(e.target.value)} autoComplete="current-password" className="glass-input h-11 w-full rounded-xl px-3 pr-11 font-mono text-sm outline-none" placeholder="••••••-••••••-••••••" required />
              <button type="button" aria-label={show ? 'Hide token' : 'Show token'} onClick={() => setShow((s) => !s)} className="absolute right-1 top-1 flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground hover:text-primary">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </label>
          {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
          <button disabled={busy} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition hover:bg-primary/90 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} Sign in
          </button>
        </form>
        {needsSetup && (
          <button onClick={setup} disabled={busy} className="mt-4 w-full rounded-xl border border-dashed border-primary/40 px-3 py-2.5 text-sm font-medium text-primary hover:bg-primary/5">
            First-time setup: create the administrator account
          </button>
        )}
        <p className="mt-7 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" /> Confidential — authorized personnel only
        </p>
      </div>
      {revealed && <TokenReveal title="Administrator account created" username={revealed.username} token={revealed.token} onClose={() => setRevealed(null)} />}
    </div>
  );
}
